import { DefaultNamespace, TFunction } from 'i18next'
import { z } from 'zod'

import { alphanumericRegex, phoneRegex } from '~/validation/regex'

type UserRoleSetting = {
  label: string
  value: string
  planCode: string
}

type MemberEdit = {
  id: string
  name: string
  englishName?: string
  email: string
  role: number
  language: number
  phoneNumber?: string
  mobileNumber?: string
  userRoleSettings: UserRoleSetting[][]
}

export const memberEditSchema = (
  t: TFunction<DefaultNamespace, undefined>,
): z.ZodSchema<MemberEdit> =>
  z.object({
    id: z.string(),
    name: z.string().min(1, {
      message: t('message.required', {
        entity: t('pages.members.label.name'),
      }),
    }),
    englishName: z
      .string()
      .max(128, {
        message: t('message.max_length', {
          entity: t('pages.members.label.englishName'),
        }),
      })
      .refine(
        (val) => alphanumericRegex.test(val),
        t('message.alphanumeric', {
          entity: t('pages.members.label.englishName'),
        }) ?? '',
      )
      .optional(),
    email: z
      .string()
      .min(1, {
        message: t('message.required', {
          entity: t('pages.members.label.email'),
        }),
      })
      .email(t('message.invalidEmail') ?? ''),
    role: z.coerce.number(),
    language: z.coerce.number(),
    phoneNumber: z
      .string()
      .optional()
      .refine(
        (val) => val === undefined || val === '' || phoneRegex.test(val),
        t('message.invalidPhoneNumber') ?? '',
      ),
    mobileNumber: z
      .string()
      .optional()
      .refine(
        (val) => val === undefined || val === '' || phoneRegex.test(val),
        t('message.invalidPhoneNumber') ?? '',
      ),
    userRoleSettings: z.array(
      z
        .array(
          z.object({
            label: z.string(),
            value: z.string(),
            planCode: z.string(),
          }),
        )
        .min(1, {
          message: t('message.min1', {
            entity: t('pages.members.label.userRole'),
          }),
        }),
    ),
  }) satisfies z.ZodSchema<MemberEdit>

export type MemberEditSchema = z.infer<ReturnType<typeof memberEditSchema>>
